/**
 * Notion MCP Service
 * Handles communication with the Notion MCP API Gateway.
 */

const NOTION_MCP_BASE_URL =
  'https://r9gafjgrkl.execute-api.us-east-1.amazonaws.com/dev'
const TENANT_ID_KEY = 'vsm_notion_tenant_id'
export interface NotionCallResult<T = any> {
  success: boolean
  data?: T
  error?: string
  rawData?: any // Raw server response before unwrapping (for debug)
}

/** Default tenant — API key managed server-side, no OAuth needed */
const DEFAULT_TENANT_ID = 'test-tenant'

/** Get the stored tenant ID (falls back to default) */
export function getNotionTenantId(): string {
  try {
    return localStorage.getItem(TENANT_ID_KEY) || DEFAULT_TENANT_ID
  } catch {
    return DEFAULT_TENANT_ID
  }
}

/** Check if Notion is connected — always true, API key is server-side */
export function isNotionConnected(): boolean {
  return true
}

/** Store tenant ID */
export function setNotionTenantId(tenantId: string): void {
  try {
    localStorage.setItem(TENANT_ID_KEY, tenantId)
  } catch {}
}

/** Clear Notion connection */
export function clearNotionConnection(): void {
  try {
    localStorage.removeItem(TENANT_ID_KEY)
  } catch {}
}

/** Helper: delay for ms */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** Retryable status codes (transient server errors) */
const RETRYABLE_STATUS_CODES = [
  408, 429, 500, 502, 503, 504, 520, 521, 522, 524,
]

/**
 * Call a Notion MCP tool with automatic retry for transient errors.
 */
export async function notionCall<T = any>(
  toolName: string,
  params: Record<string, any> = {},
  overrideTenantId?: string,
  maxRetries: number = 2,
): Promise<NotionCallResult<T>> {
  const tenantId = overrideTenantId || getNotionTenantId()
  let lastError = ''
  const callStart = performance.now()
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 15000)
      const response = await fetch(`${NOTION_MCP_BASE_URL}/tool/${toolName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          params: {
            tenant_id: tenantId,
            ...params,
          },
        }),
        signal: controller.signal,
      })
      clearTimeout(timeoutId)
      if (!response.ok) {
        const errorText = await response.text().catch(() => response.statusText)
        lastError = `Notion MCP error ${response.status}: ${errorText}`
        if (
          RETRYABLE_STATUS_CODES.includes(response.status) &&
          attempt < maxRetries
        ) {
          const backoff = Math.min(1000 * Math.pow(2, attempt), 4000)
          console.warn(
            `[Notion] ${lastError} — retrying in ${backoff}ms (attempt ${attempt + 1}/${maxRetries})`,
          )
          await delay(backoff)
          continue
        }
        return {
          success: false,
          error: lastError,
        }
      }
      const rawData = await response.json()
      const durationMs = Math.round(performance.now() - callStart)

      // Unwrap MCP response format if needed
      const data = unwrapMcpResponse<T>(rawData)

      // Log to debug console
      logNotion(toolName, params, rawData, data, true, undefined, durationMs)
      return {
        success: true,
        data,
        rawData,
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        lastError = 'La solicitud a Notion tardó demasiado (timeout 15s)'
      } else {
        lastError = err.message || 'Error de conexión con Notion MCP'
      }
      if (attempt < maxRetries) {
        const backoff = Math.min(1000 * Math.pow(2, attempt), 4000)
        await delay(backoff)
        continue
      }
    }
  }
  const durationMs = Math.round(performance.now() - callStart)
  logNotion(
    toolName,
    params,
    null,
    null,
    false,
    lastError || 'Error de conexión con Notion MCP después de varios intentos',
    durationMs,
  )
  return {
    success: false,
    error:
      lastError ||
      'Error de conexión con Notion MCP después de varios intentos',
  }
}
function logNotion(
  tool: string,
  params: any,
  rawResponse: any,
  processedData: any,
  success: boolean,
  error?: string,
  durationMs?: number,
) {
  try {
    import('../hooks/useDebugLogs').then(({ pushDebugLog }) => {
      pushDebugLog({
        source: 'notion',
        tool,
        params,
        rawResponse,
        processedData,
        success,
        error,
        durationMs,
      })
    })
  } catch {}
}

/**
 * Unwrap MCP-style response envelope.
 * MCP tools may return data in many nested formats. This function
 * recursively unwraps until it finds the actual Notion data.
 */
function unwrapMcpResponse<T = any>(raw: any): T {
  if (!raw || typeof raw !== 'object') return raw

  // Format: { content: [{ type: "text", text: "..." }] }
  // This is the standard MCP tool response format
  if (Array.isArray(raw.content) && raw.content.length > 0) {
    const firstContent = raw.content[0]
    if (
      firstContent?.type === 'text' &&
      typeof firstContent.text === 'string'
    ) {
      try {
        const parsed = JSON.parse(firstContent.text)
        // Recursively unwrap in case it's double-wrapped
        return unwrapMcpResponse(parsed)
      } catch {
        // Not valid JSON, return as-is
        return raw
      }
    }
  }

  // Format: { body: "..." } (API Gateway sometimes stringifies the body)
  if (typeof raw.body === 'string') {
    try {
      const parsed = JSON.parse(raw.body)
      return unwrapMcpResponse(parsed)
    } catch {
      return raw
    }
  }

  // Format: { result: { content: [...] } } or { result: { results: [...] } }
  if (raw.result && typeof raw.result === 'object' && !raw.results) {
    return unwrapMcpResponse(raw.result)
  }

  // Format: { data: { ... } } but NOT when we already have results at top level
  if (
    raw.data &&
    typeof raw.data === 'object' &&
    !raw.results &&
    !Array.isArray(raw)
  ) {
    return unwrapMcpResponse(raw.data)
  }

  // Already in a usable format: { results: [...] } or an array or a plain object
  return raw
}
